/**
 * Ende-zu-Ende-Abläufe für die Abnahme (docs/abnahme-plan.md, Abschnitt 15).
 *
 * Ein Ablauf ist eine Kette von Schritten; jeder Schritt nennt die Stories, die
 * ihn abdecken. Status und Testabdeckung kommen live aus dem Katalog — hier steht
 * nur, welcher Schritt zu welchen Story-Schlüsseln gehört. Eine Story darf in
 * mehreren Abläufen vorkommen. Schlüssel, die es im Katalog nicht (mehr) gibt,
 * zeigt die Seite als Warnung an, statt sie still zu verschlucken.
 */

export type Akteur = 'website' | 'kunde' | 'office' | 'lehrkraft' | 'system' | 'admin';

export const AKTEUR_LABEL: Record<Akteur, string> = {
  website: 'Website',
  kunde: 'Kundenbereich',
  office: 'Office',
  lehrkraft: 'Lehrkraft',
  system: 'Automatisch',
  admin: 'Einrichtung',
};

export interface AblaufSchritt {
  titel: string;
  akteur: Akteur;
  stories: string[];
  /** Nebenweg: passiert nicht in jedem Durchlauf (verschieben, absagen, mahnen). */
  nebenweg?: boolean;
}

export interface Ablauf {
  key: string;
  titel: string;
  beschreibung: string;
  schritte: AblaufSchritt[];
}

export const ABLAEUFE: Ablauf[] = [
  {
    key: 'anfrage-probestunde',
    titel: 'Von der Anfrage zur Probestunde',
    beschreibung:
      'Ein Interessent fragt auf der Website an, das Office nimmt Kontakt auf und plant die Probestunde; danach wird entschieden.',
    schritte: [
      { titel: 'Anfrage stellen', akteur: 'website', stories: ['ANF-2', 'ANF-8'] },
      { titel: 'Anfrage sichten', akteur: 'office', stories: ['ANF-1', 'ANF-5', 'ANF-6', 'ANF-7'] },
      { titel: 'Kontakt aufnehmen', akteur: 'office', stories: ['ANF-3', 'ANF-4'] },
      { titel: 'Probestunde planen', akteur: 'office', stories: ['PS-1', 'PS-2', 'PS-3'] },
      { titel: 'Erinnern', akteur: 'system', stories: ['PS-7'] },
      { titel: 'Verschieben oder absagen', akteur: 'office', stories: ['PS-4', 'PS-5'], nebenweg: true },
      { titel: 'Entscheiden', akteur: 'office', stories: ['PS-6'] },
    ],
  },
  {
    key: 'probestunde-start',
    titel: 'Von der Probestunde zum Unterrichtsstart',
    beschreibung:
      'Das Office stellt ein Angebot zusammen, der Kunde schließt die Anmeldung im Kundenbereich ab, die Lehrkraft nimmt den Auftrag an.',
    schritte: [
      { titel: 'Angebot erstellen', akteur: 'office', stories: ['ANM-1', 'ANM-2'] },
      { titel: 'Versenden und erinnern', akteur: 'system', stories: ['ANM-3'] },
      { titel: 'Konto und Anmeldung', akteur: 'kunde', stories: ['KUNDE-1', 'KUNDE-2', 'KUNDE-13'] },
      { titel: 'Auftrag an die Lehrkraft', akteur: 'lehrkraft', stories: ['ANM-4', 'LEHR-2'] },
      { titel: 'Unterricht starten', akteur: 'office', stories: ['ANM-5'] },
    ],
  },
  {
    key: 'umstieg',
    titel: 'Umstieg aus dem Altsystem',
    beschreibung:
      'Bestandskunden ziehen mit laufendem Vertrag um: Das Büro passt den Vertrag an, der Kunde bestätigt ihn im Kundenbereich, die Lehrkraft sagt zu, und mit dem Kursstart läuft der Unterricht im neuen System weiter.',
    schritte: [
      { titel: 'Altdaten übernehmen', akteur: 'admin', stories: ['ADM-5'] },
      { titel: 'Fälle sichten', akteur: 'office', stories: ['MIG-1'] },
      { titel: 'Vertrag anpassen und einladen', akteur: 'office', stories: ['MIG-2'] },
      { titel: 'Vertrag bestätigen', akteur: 'kunde', stories: ['KUNDE-5'] },
      { titel: 'Angebot an die Lehrkraft', akteur: 'lehrkraft', stories: ['MIG-3', 'LEHR-2'] },
      { titel: 'Vertragskonto', akteur: 'system', stories: ['MIG-5'] },
      { titel: 'Kurs starten', akteur: 'office', stories: ['MIG-4'] },
    ],
  },
  {
    key: 'unterricht',
    titel: 'Laufender Unterricht',
    beschreibung: 'Kurse stehen im Stundenplan, Lehrkräfte arbeiten im Portal, Termine werden erfasst oder geändert.',
    schritte: [
      { titel: 'Stundenplan', akteur: 'office', stories: ['TERM-2', 'TERM-1', 'TERM-8', 'TERM-7'] },
      { titel: 'Räume', akteur: 'office', stories: ['RAUM-1', 'RAUM-2'] },
      { titel: 'Lehrerportal', akteur: 'lehrkraft', stories: ['LEHR-3'] },
      { titel: 'Termine erfassen', akteur: 'lehrkraft', stories: ['TERM-5'] },
      {
        titel: 'Verschieben, absagen, Lehrkraft wechseln',
        akteur: 'office',
        stories: ['TERM-3', 'TERM-4', 'TERM-6'],
        nebenweg: true,
      },
    ],
  },
  {
    key: 'zahlung',
    titel: 'Vertrag und Zahlung',
    beschreibung:
      'Aus der Anmeldung entsteht ein Vertragskonto; Beiträge oder Kontingente werden gebucht, eingezogen, abgeglichen und belegt.',
    schritte: [
      { titel: 'Abrechnung einrichten', akteur: 'admin', stories: ['BUCH-4', 'BUCH-5', 'STO-7'] },
      { titel: 'Vertragskonto', akteur: 'system', stories: ['BUCH-1'] },
      { titel: 'Zahlungsweg', akteur: 'kunde', stories: ['BUCH-8', 'KUNDE-11', 'BUCH-14'] },
      { titel: 'Beitrag oder Kontingent', akteur: 'system', stories: ['BUCH-6', 'BUCH-7'] },
      { titel: 'Zahlungslauf', akteur: 'office', stories: ['BUCH-9', 'BUCH-13'] },
      { titel: 'Kontoauszug und Rücklastschrift', akteur: 'office', stories: ['BUCH-10'] },
      { titel: 'Mahnen', akteur: 'system', stories: ['BUCH-2'], nebenweg: true },
      { titel: 'Belege und Buchhaltung', akteur: 'office', stories: ['BUCH-3', 'BUCH-12'] },
      { titel: 'Im Kundenbereich', akteur: 'kunde', stories: ['KUNDE-10'] },
    ],
  },
  {
    key: 'kuendigung',
    titel: 'Kündigung',
    beschreibung: 'Kunde oder Office kündigt, die Frist wird berechnet, zum Stichtag endet der Vertrag sauber.',
    schritte: [
      { titel: 'Fristen festlegen', akteur: 'admin', stories: ['STO-5'] },
      { titel: 'Kündigung erfassen', akteur: 'kunde', stories: ['KUNDE-4', 'KUE-1'] },
      { titel: 'Überblicken', akteur: 'office', stories: ['KUE-3'] },
      { titel: 'Wirksam zum Stichtag', akteur: 'system', stories: ['KUE-2'] },
      { titel: 'Resteinheiten und Erstattung', akteur: 'office', stories: ['BUCH-11'] },
    ],
  },
  {
    key: 'honorar',
    titel: 'Honorar der Lehrkraft',
    beschreibung: 'Durchgeführte Termine werden zur Honorarrechnung, die die Lehrkraft im Portal erstellt und versendet.',
    schritte: [
      { titel: 'Lehrkraft einrichten', akteur: 'office', stories: ['LEHR-1'] },
      { titel: 'Termine durchgeführt', akteur: 'lehrkraft', stories: ['TERM-5'] },
      { titel: 'Honorarrechnung', akteur: 'lehrkraft', stories: ['LEHR-4'] },
    ],
  },
];
